import React, { useEffect, useState, useRef } from 'react';
import {
   Play, CheckCircle2, Globe, Activity, Layers, ArrowRight, ShieldCheck,
   Database, LayoutDashboard, ChevronDown, Scan, Upload, Cloud, MonitorPlay,
   Truck, Star, Cpu, Quote, Check, Package, FileText, Settings, PenTool,
   Bell, Plus, Clock, Search, LayoutList, MoreVertical, Phone, MessageCircle,
   Zap, BarChart3, Users, Lock, Image as ImageIcon, ExternalLink, MousePointer2
} from 'lucide-react';
import { translations, Language, Direction } from '../services/translations';
import { api } from '../services/api';
import { supabase } from '../services/supabase';
import { SystemSettings } from '../types';

// --- Custom CSS for complex animations ---
const customStyles = `
  @keyframes aurora {
    0% { background-position: 50% 50%, 50% 50%; }
    50% { background-position: 100% 0%, 0% 100%; }
    100% { background-position: 50% 50%, 50% 50%; }
  }
  @keyframes marquee {
    from { transform: translateX(0); }
    to { transform: translateX(-50%); }
  }
  @keyframes shimmer {
    from { background-position: 0 0; }
    to { background-position: -200% 0; }
  }
  @keyframes fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes slide-up {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .animate-aurora {
    animation: aurora 20s infinite alternate;
  }
  .animate-marquee {
    animation: marquee 30s linear infinite;
  }
  .animate-shimmer {
    animation: shimmer 2s linear infinite;
  }
  .animate-fade-in {
    animation: fade-in 1s ease-out forwards;
  }
  .animate-slide-up {
    animation: slide-up 1s ease-out 0.2s forwards;
  }
  .perspective-1000 {
    perspective: 1000px;
  }
  .rotate-x-12 {
     transform: rotateX(12deg);
  }
  .scrollbar-hide::-webkit-scrollbar {
      display: none;
  }
  .text-glow {
    text-shadow: 0 0 30px rgba(255,255,255,0.3);
  }
`;

// --- Reusable Components ---

const Navbar = ({ onLogin, onGetStarted, scrolled, lang, setLang }: any) => {
   const [settings, setSettings] = useState<SystemSettings | null>(null);

   useEffect(() => {
      api.getSettings().then(setSettings);
   }, []);

   return (
      <nav className={`fixed w-full top-0 z-50 transition-all duration-500 ${scrolled ? 'py-3 bg-black/50 backdrop-blur-xl border-b border-white/5' : 'py-6 bg-transparent'}`}>
         <div className="max-w-[1400px] mx-auto px-6 flex justify-between items-center">
            <div className="flex items-center gap-3">
               <div className="flex items-center justify-center">
                  <img src="/logo-dark-mode.png" alt="Galal Lab" className="h-10 md:h-14 w-auto object-contain" />
               </div>
            </div>

            <div className="hidden md:flex items-center gap-8 text-sm font-medium text-zinc-400">
               <a href="#features" className="hover:text-white transition-colors duration-300">{translations[lang].landing.navFeatures}</a>
               <a href="#showcase" className="hover:text-white transition-colors duration-300">{translations[lang].landing.navShowcase}</a>
               <a href="#workflow" className="hover:text-white transition-colors duration-300">{translations[lang].landing.navWorkflow}</a>
            </div>

            <div className="flex items-center gap-4">
               {/* Phone Number Display (Desktop) */}
               <div className="hidden lg:flex items-center gap-2 text-sm font-bold text-zinc-400 border-e border-zinc-800 pe-4 me-1">
                  <Phone size={16} className="text-zinc-500" />
                  <span dir="ltr">+20 15 567 6 1102</span>
               </div>

               <button onClick={() => setLang(lang === 'en' ? 'ar' : 'en')} className="text-sm font-medium text-zinc-400 hover:text-white transition-colors uppercase">
                  {lang === 'en' ? 'العربية' : 'English'}
               </button>
               <button onClick={onLogin} className="hidden md:block text-sm font-medium text-zinc-300 hover:text-white px-4 py-2 transition-colors">
                  {translations[lang].landing.login}
               </button>
               <button onClick={onGetStarted} className="bg-white text-black px-5 py-2.5 rounded-full text-sm font-bold hover:scale-105 transition-all shadow-[0_0_20px_rgba(255,255,255,0.4)]">
                  {translations[lang].landing.getStarted}
               </button>
            </div>
         </div>
      </nav>
   );
};

// Spotlight Card Effect
const SpotlightCard = ({ children, className = "", title, subtitle, icon: Icon, colSpan = 1 }: any) => {
   const divRef = useRef<HTMLDivElement>(null);
   const [isFocused, setIsFocused] = useState(false);
   const [position, setPosition] = useState({ x: 0, y: 0 });
   const [opacity, setOpacity] = useState(0);

   const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!divRef.current) return;
      const div = divRef.current;
      const rect = div.getBoundingClientRect();
      setPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
   };

   const handleFocus = () => {
      setIsFocused(true);
      setOpacity(1);
   };

   const handleBlur = () => {
      setIsFocused(false);
      setOpacity(0);
   };

   const handleMouseEnter = () => {
      setOpacity(1);
   };

   const handleMouseLeave = () => {
      setOpacity(0);
   };

   return (
      <div
         ref={divRef}
         onMouseMove={handleMouseMove}
         onFocus={handleFocus}
         onBlur={handleBlur}
         onMouseEnter={handleMouseEnter}
         onMouseLeave={handleMouseLeave}
         className={`relative rounded-3xl border border-white/10 bg-zinc-900/50 overflow-hidden group ${className}`}
      >
         <div
            className="pointer-events-none absolute -inset-px opacity-0 transition-opacity duration-300 group-hover:opacity-100"
            style={{
               background: `radial-gradient(600px circle at ${position.x}px ${position.y}px, rgba(255,255,255,0.1), transparent 40%)`,
            }}
         />
         <div className="relative h-full flex flex-col p-8">
            <div className="mb-6 flex items-center justify-between">
               {Icon && <div className="p-3 bg-white/5 rounded-2xl text-white ring-1 ring-white/10"><Icon size={24} /></div>}
               <div className={`p-2 rounded-full transition-colors ${isFocused || opacity ? 'bg-white/10' : 'bg-transparent'}`}>
                  <ArrowRight size={16} className={`text-zinc-500 transition-transform ${isFocused || opacity ? '-rotate-45 text-white' : ''}`} />
               </div>
            </div>
            <h3 className="text-2xl font-bold text-white mb-2 tracking-tight">{title}</h3>
            <p className="text-zinc-400 text-sm leading-relaxed mb-6">{subtitle}</p>
            <div className="flex-1 mt-auto">
               {children}
            </div>
         </div>
      </div>
   );
};

const CaseCard = ({ image, title, type, technician }: any) => (
   <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-zinc-900 border border-white/10 group cursor-pointer">
      {image ? (
         <img src={image} alt={title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
      ) : (
         <div className="w-full h-full flex items-center justify-center text-zinc-800 bg-zinc-900">
            <div className="text-center">
               <ImageIcon size={64} className="mb-4 mx-auto opacity-20" />
               <span className="text-zinc-700 text-sm font-mono uppercase tracking-widest">No Image</span>
            </div>
         </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent flex flex-col justify-end p-8 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
         <h4 className="text-white font-bold text-xl mb-1">{title}</h4>
         <p className="text-zinc-400 text-sm">{type}</p>
      </div>
   </div>
);

// --- Main Page Component ---

export const LandingPage = ({ onGetStarted, onLogin, lang, setLang }: { onGetStarted: () => void, onLogin: () => void, lang: Language, setLang: (l: Language) => void }) => {
   const t = translations[lang];
   const isRTL = lang === 'ar';
   const [scrollY, setScrollY] = useState(0);
   const [openFaq, setOpenFaq] = useState<number | null>(null);

   useEffect(() => {
      const handleScroll = () => setScrollY(window.scrollY);
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
   }, []);

   return (
      <div dir={Direction[lang]} className={`bg-black text-white min-h-screen selection:bg-white/20 ${isRTL ? 'font-arabic' : 'font-sans'}`}>
         <style>{customStyles}</style>
         <Navbar onLogin={onLogin} onGetStarted={onGetStarted} scrolled={scrollY > 20} lang={lang} setLang={setLang} />

         {/* --- HERO SECTION V2 (Aurora) --- */}
         <section className="relative min-h-[90vh] flex flex-col items-center justify-center pt-32 px-6 overflow-hidden">
            {/* Aurora Background */}
            <div className="absolute inset-0 bg-black">
               <div className="absolute top-[-10%] start-[-10%] w-[40%] h-[40%] bg-zinc-800/30 rounded-full blur-[120px] animate-aurora mix-blend-screen"></div>
               <div className="absolute bottom-[-10%] end-[-10%] w-[40%] h-[40%] bg-zinc-600/10 rounded-full blur-[120px] animate-aurora mix-blend-screen" style={{ animationDelay: '-5s' }}></div>
               <div className="absolute top-[20%] end-[20%] w-[30%] h-[30%] bg-white/5 rounded-full blur-[100px] animate-aurora mix-blend-screen" style={{ animationDelay: '-10s' }}></div>
            </div>

            {/* Grid Pattern Overlay */}
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none"></div>

            <div className="max-w-6xl mx-auto text-center relative z-10">
               <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-zinc-300 text-xs font-semibold uppercase tracking-widest mb-10 backdrop-blur-md hover:bg-white/10 transition-colors cursor-default">
                  <span className="w-2 h-2 rounded-full bg-white animate-pulse"></span>
                  {t.landing.badge}
               </div>

               <h1
                  className="font-heading text-6xl md:text-8xl lg:text-9xl font-bold tracking-tighter text-transparent bg-clip-text bg-gradient-to-b from-white via-white to-white/40 mb-8 leading-[0.9]"
                  dangerouslySetInnerHTML={{ __html: t.landing.heroTitle }}
               />

               <p
                  className="text-xl md:text-2xl text-zinc-400 max-w-2xl mx-auto mb-14 leading-relaxed font-light"
                  dangerouslySetInnerHTML={{ __html: t.landing.heroDesc }}
               />

               <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-24">
                  <button onClick={onGetStarted} className="group relative px-8 py-4 bg-white text-black rounded-full font-bold text-lg hover:scale-105 transition-all w-full sm:w-auto overflow-hidden">
                     <span className="relative z-10 flex items-center justify-center gap-2">{t.landing.getStarted} <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform rtl:rotate-180" /></span>
                     <div className="absolute inset-0 bg-zinc-200 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  </button>
                  <button onClick={onLogin} className="px-8 py-4 text-zinc-400 hover:text-white transition-colors text-lg font-medium flex items-center gap-2">
                     <Play size={18} className="fill-current" /> {t.landing.watchShowreel}
                  </button>
               </div>
            </div>


         </section>

         {/* --- FEATURES GRID V2 (Interactive) --- */}
         <section id="features" className="py-32 px-6 bg-black relative">
            <div className="max-w-[1400px] mx-auto">
               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-[minmax(300px,auto)]">

                  {/* Dashboard Card */}
                  <SpotlightCard
                     className="md:col-span-2 lg:col-span-2 min-h-[500px]"
                     title={t.landing.features.dashboard.title}
                     subtitle={t.landing.features.dashboard.subtitle}
                     icon={LayoutDashboard}
                  >
                     <div className="w-full h-full bg-[#09090b] rounded-t-xl border border-white/5 shadow-2xl overflow-hidden relative group-hover:scale-[1.02] transition-transform duration-500">
                        {/* Abstract UI Representation */}
                        <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(68,68,68,.2)_50%,transparent_75%,transparent_100%)] bg-[length:250%_250%,100%_100%] animate-[shimmer_3s_infinite]"></div>
                        <div className="p-6 space-y-4">
                           <div className="flex items-center gap-4 mb-8">
                              <div className="w-32 h-8 bg-zinc-800 rounded-lg animate-pulse"></div>
                              <div className="ms-auto flex gap-2">
                                 <div className="w-8 h-8 rounded-full bg-zinc-800"></div>
                                 <div className="w-8 h-8 rounded-full bg-zinc-800"></div>
                              </div>
                           </div>
                           <div className="grid grid-cols-3 gap-4">
                              {[1, 2, 3].map(i => (
                                 <div key={i} className="h-32 bg-zinc-800/50 rounded-xl border border-white/5"></div>
                              ))}
                           </div>
                           <div className="h-48 bg-zinc-800/30 rounded-xl border border-white/5 mt-4"></div>
                        </div>
                     </div>
                  </SpotlightCard>

                  <SpotlightCard
                     title={t.landing.features.financial.title}
                     subtitle={t.landing.features.financial.subtitle}
                     icon={BarChart3}
                  >
                     <div className="flex items-end justify-between gap-2 h-32 px-4 pb-4">
                        {[40, 70, 45, 90, 60, 80].map((h, i) => (
                           <div key={i} className="w-full bg-green-500/20 rounded-t-sm relative overflow-hidden">
                              <div className="absolute bottom-0 w-full bg-green-500 transition-all duration-1000" style={{ height: `${h}%` }}></div>
                           </div>
                        ))}
                     </div>
                  </SpotlightCard>

                  <SpotlightCard
                     title={t.landing.features.collaboration.title}
                     subtitle={t.landing.features.collaboration.subtitle}
                     icon={Users}
                  >
                     <div className="relative h-40 mt-4">
                        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 bg-blue-500/20 rounded-full blur-xl animate-pulse"></div>
                        <div className="absolute inset-0 flex items-center justify-center">
                           <div className="flex -space-x-4">
                              <div className="w-12 h-12 rounded-full border-4 border-zinc-900 bg-zinc-700"></div>
                              <div className="w-12 h-12 rounded-full border-4 border-zinc-900 bg-zinc-600"></div>
                              <div className="w-12 h-12 rounded-full border-4 border-zinc-900 bg-zinc-500 flex items-center justify-center text-xs font-bold">+5</div>
                           </div>
                        </div>
                     </div>
                  </SpotlightCard>

                  <SpotlightCard
                     className="md:col-span-2"
                     title={t.landing.features.workflow.title}
                     subtitle={t.landing.features.workflow.subtitle}
                     icon={Zap}
                  >
                     <div className="flex items-center justify-between mt-10 px-10 opacity-60">
                        <Scan size={48} />
                        <div className="h-px bg-gradient-to-r from-transparent via-white/50 to-transparent flex-1 mx-8"></div>
                        <Upload size={48} />
                        <div className="h-px bg-gradient-to-r from-transparent via-white/50 to-transparent flex-1 mx-8"></div>
                        <CheckCircle2 size={48} />
                        <div className="h-px bg-gradient-to-r from-transparent via-white/50 to-transparent flex-1 mx-8"></div>
                        <Truck size={48} />
                     </div>
                  </SpotlightCard>
               </div>
            </div>
         </section>

         {/* --- STICKY PARALLAX SHOWCASE --- */}
         <section id="showcase" className="relative bg-[#050505] py-32">
            <div className="max-w-[1400px] mx-auto px-6">
               <div className="flex flex-col lg:flex-row gap-20">
                  {/* Sticky Left Content */}
                  <div className="lg:w-1/3 selection:bg-pink-500/30">
                     <div className="sticky top-32">
                        <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mb-8 backdrop-blur-md">
                           <Star className="text-yellow-400 fill-yellow-400" />
                        </div>
                        <h2
                           className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight"
                           dangerouslySetInnerHTML={{ __html: t.landing.showcase.title }}
                        />
                        <p className="text-lg text-zinc-400 leading-relaxed mb-10">
                           {t.landing.showcase.description}
                        </p>

                        <div className="space-y-6">
                           <div className="flex items-center gap-4 text-zinc-300">
                              <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400"><Check size={16} /></div>
                              <span>{t.landing.showcase.checks.viewer}</span>
                           </div>
                           <div className="flex items-center gap-4 text-zinc-300">
                              <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400"><Check size={16} /></div>
                              <span>{t.landing.showcase.checks.comparison}</span>
                           </div>
                           <div className="flex items-center gap-4 text-zinc-300">
                              <div className="w-8 h-8 rounded-full bg-pink-500/20 flex items-center justify-center text-pink-400"><Check size={16} /></div>
                              <span>{t.landing.showcase.checks.portfolios}</span>
                           </div>
                        </div>

                        <button className="mt-12 text-white border-b border-white pb-1 hover:text-zinc-300 hover:border-zinc-300 transition-colors">
                           {t.landing.showcase.viewGallery}
                        </button>
                     </div>
                  </div>

                  {/* Right Scrollable Content */}
                  <div className="lg:w-2/3 space-y-12">
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {t.landing.showcase.cases.map((item: any, idx: number) => {
                           const filenames = ['0.png', '8.jpg', '2.jpg', '3.jpg', '4.jpg', '5.png', '6.png', '7.jpg'];
                           const filename = filenames[idx] || '0.png';
                           const { data } = supabase.storage.from('landing cases').getPublicUrl(filename);

                           return (
                              <CaseCard
                                 key={idx}
                                 title={item.title}
                                 type={item.type}
                                 image={data.publicUrl}
                              />
                           );
                        })}
                     </div>
                  </div>
               </div>
            </div>
         </section>

         {/* --- INTEGRATION MARQUEE --- */}
         <section className="py-24 bg-black border-y border-white/5 overflow-hidden">
            <div className="max-w-[1400px] mx-auto px-6 mb-12 text-center">
               <p className="text-sm font-bold text-zinc-500 uppercase tracking-[0.2em]">{t.landing.integrations.title}</p>
            </div>
            <div className="relative flex overflow-x-hidden group">
               <div className="animate-marquee whitespace-nowrap flex gap-32 items-center opacity-40 grayscale group-hover:grayscale-0 transition-all duration-700">
                  {/* Duplicated needed for infinity scroll effect in real implementation, simplified here */}
                  <span className="text-5xl font-bold text-white">3Shape</span>
                  <span className="text-5xl font-bold text-white">exocad</span>
                  <span className="text-5xl font-bold text-white">Medit</span>
                  <span className="text-5xl font-bold text-white">iTero</span>
                  <span className="text-5xl font-bold text-white">Dentsply</span>
                  <span className="text-5xl font-bold text-white">Planmeca</span>
                  <span className="text-5xl font-bold text-white">Carestream</span>
                  <span className="text-5xl font-bold text-white">3Shape</span>
                  <span className="text-5xl font-bold text-white">exocad</span>
               </div>
            </div>
         </section>

         {/* --- FAQ SECTION --- */}
         <section id="faq" className="py-32 px-6 bg-[#050505]">
            <div className="max-w-4xl mx-auto">
               <h2 className="text-4xl font-bold text-white mb-16 text-center">{t.landing.faq.title}</h2>
               <div className="grid gap-6">
                  {t.landing.faq.items.map((item: any, idx: number) => (
                     <div key={idx} className="group bg-zinc-900/30 border border-white/5 rounded-2xl overflow-hidden hover:bg-zinc-900/60 transition-colors">
                        <div
                           className="p-8 flex justify-between items-center cursor-pointer"
                           onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                        >
                           <h3 className="text-xl font-medium text-zinc-200 group-hover:text-white transition-colors">{item.q}</h3>
                           <Plus className={`text-zinc-500 group-hover:text-white transition-all duration-300 ${openFaq === idx ? 'rotate-45' : 'rotate-0'}`} />
                        </div>
                        <div
                           className={`grid transition-[grid-template-rows] duration-300 ease-out ${openFaq === idx ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
                        >
                           <div className="overflow-hidden">
                              <p className="px-8 pb-8 text-zinc-400 leading-relaxed">
                                 {item.a}
                              </p>
                           </div>
                        </div>
                     </div>
                  ))}
               </div>
            </div>
         </section>

         {/* --- CTA FOOTER --- */}
         <footer className="py-20 bg-black border-t border-white/10 px-6">
            <div className="max-w-[1400px] mx-auto text-center">
               <h2 className="text-5xl md:text-8xl font-bold text-white mb-12 tracking-tighter">
                  {t.landing.footer.readyTitle}
               </h2>
               <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-20">
                  <button onClick={onGetStarted} className="px-10 py-5 bg-white text-black rounded-full font-bold text-xl hover:scale-105 transition-all w-full sm:w-auto">
                     {t.landing.footer.getStartedNow}
                  </button>
               </div>

               <div className="flex flex-col md:flex-row justify-between items-center pt-20 border-t border-white/5 text-zinc-500 text-sm">
                  <div>{t.landing.footer.copyright}</div>
                  <div className="flex gap-8 mt-4 md:mt-0">
                     <a href="#" className="hover:text-white transition-colors">{t.landing.footer.privacy}</a>
                     <a href="#" className="hover:text-white transition-colors">{t.landing.footer.terms}</a>
                  </div>
               </div>
            </div>
         </footer>

         {/* Floating WhatsApp */}
         <a
            href="https://wa.me/201556761102"
            target="_blank"
            rel="noreferrer"
            className="fixed bottom-8 right-8 z-50 bg-[#25D366] hover:bg-[#20bd5a] text-white p-4 rounded-full shadow-[0_0_30px_rgba(37,211,102,0.4)] transition-transform hover:scale-110 hover:rotate-12 flex items-center justify-center"
         >
            <MessageCircle size={32} fill="white" className="text-white" />
         </a>
      </div>
   );
};
